import { useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import { Button } from "@mui/material";
import IfCan from "../../../components/IfCan";
import {
  ResponsiveContainer,
  Tooltip,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import MainContainer from "../../../layout/MainContainer";


const dauData = [
  { name: "Mon", users: 120 },
  { name: "Tue", users: 210 },
  { name: "Wed", users: 180 },
  { name: "Thu", users: 300 },
  { name: "Fri", users: 260 },
  { name: "Sat", users: 140 },
  { name: "Sun", users: 170 },
];

const retentionData = [
  { name: "Day 1", retention: 80 },
  { name: "Day 2", retention: 60 },
  { name: "Day 3", retention: 45 },
  { name: "Day 4", retention: 35 },
  { name: "Day 5", retention: 25 },
  { name: "Day 6", retention: 20 },
  { name: "Day 7", retention: 15 },
];

const moduleUsageData = [
  { name: "Tasks", usage: 400 },
  { name: "Budgeting", usage: 300 },
  { name: "Meals", usage: 200 },
  { name: "Reminders", usage: 278 },
  { name: "Voice Notes", usage: 189 },
];

const inputBreakdownData = [
  { name: "Voice", value: 65 },
  { name: "Text", value: 35 },
];

const goalCompletionData = [
  { name: "Jan", completion: 70 },
  { name: "Feb", completion: 75 },
  { name: "Mar", completion: 68 },
  { name: "Apr", completion: 80 },
  { name: "May", completion: 74 },
  { name: "Jun", completion: 79 },
];

const COLORS = ["#0088FE", "#FF8042"];

const UserEngagement = () => {
  const navigate = useNavigate();

  return (
    <>
      <div className="main_title d_flex">
        <div className="left_s">
          <h1>User Engagement Dashboard</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> - User Engagement Dashboard
          </p>
        </div>
        <div className="right_s">
          <IfCan module="Manage Analytics" action="add_edit">
            <Button className="btn btn_primary">
              <DownloadIcon /> Export
            </Button>
          </IfCan>
        </div>
      </div>


      <div className="grid_sc gap_m">
        {/* 1. Daily Active Users */}
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Daily Active Users</h2>
            </div>
            <div className="right_s">
              <p>
                <span>A day</span>
                <span>A week</span>
                <span>A month</span>
                <span>A year</span>
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dauData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="users" fill="#38B6FF" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 2. Retention Curve */}
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Retention Curve</h2>
            </div>
            <div className="right_s">
              <p>
                <span>A day</span>
                <span>A week</span>
                <span>A month</span>
                <span>A year</span>
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={retentionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="retention" stroke="#FF6384" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* 3. Most Used Modules */}
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Most Used Modules</h2>
            </div>
            <div className="right_s">
              <p>
                <span>A day</span>
                <span>A week</span>
                <span>A month</span>
                <span>A year</span>
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={moduleUsageData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="usage" fill="#4BC0C0" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 4. Voice vs Text Input */}
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Voice vs Text Input</h2>
            </div>
            <div className="right_s">
              <p>
                <span>A day</span>
                <span>A week</span>
                <span>A month</span>
                <span>A year</span>
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={inputBreakdownData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label
              >
                {inputBreakdownData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* 5. Goal Completion Rates */}
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Goal Completion Rates</h2>
            </div>
            <div className="right_s">
              <p>
                <span>A day</span>
                <span>A week</span>
                <span>A month</span>
                <span>A year</span>
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={goalCompletionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Line type="monotone" dataKey="completion" stroke="#00C49F" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

export default UserEngagement;
